/**
 * RoleGate — conditional rendering by user role or permission.
 *
 * The codebase previously open-coded `user.role === 'admin'` checks at
 * each call site. RoleGate replaces those with a declarative component
 * that:
 *
 *   1. Resolves the current user via AuthContext.
 *   2. Renders children only when the user matches the role/permission.
 *   3. Renders a `fallback` (or nothing) otherwise.
 *   4. Renders nothing while auth is still loading — prevents the
 *      "admin UI flashes for a viewer before disappearing" race.
 *
 * Usage:
 *
 *     <RoleGate role="admin">
 *       <DangerousButton />
 *     </RoleGate>
 *
 *     <RoleGate role={['admin', 'editor']}>
 *       <EditPanel />
 *     </RoleGate>
 *
 *     <RoleGate permission="qr:delete">
 *       <DeleteAction />
 *     </RoleGate>
 *
 *     <RoleGate role="admin" fallback={<UnauthorizedNotice />}>
 *       <AdminPanel />
 *     </RoleGate>
 *
 * RoleGate is a UX primitive — the backend is the actual source of
 * truth on authorization. Use it to hide UI a user cannot meaningfully
 * use, not to enforce security.
 */

import React from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * @param {object} props
 * @param {string|string[]} [props.role]       Single role or array of allowed roles.
 * @param {string}          [props.permission] A permission string from the user's permissions list.
 * @param {React.ReactNode} [props.fallback]   Element shown when the gate denies.
 * @param {React.ReactNode} props.children     Element shown when the gate allows.
 */
export function RoleGate({ role, permission, fallback = null, children }) {
  const { user, loading } = useAuth();

  // Don't flash protected UI before auth resolves.
  if (loading) return null;

  if (!user) return fallback;

  const allows = checkAccess(user, { role, permission });
  return allows ? children : fallback;
}

/**
 * Hook form for places where conditional rendering is awkward (e.g.
 * computing menu item arrays).
 *
 *     const canManageUsers = useRoleAccess({ role: 'admin' });
 */
export function useRoleAccess({ role, permission } = {}) {
  const { user, loading } = useAuth();
  if (loading || !user) return false;
  return checkAccess(user, { role, permission });
}

function checkAccess(user, { role, permission }) {
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) {
      // Admins are not implicitly granted other roles — explicit list only.
      return false;
    }
  }

  if (permission) {
    // Admins implicitly pass any permission check (mirrors AuthContext's
    // hasPermission()). For non-admins, the explicit list rules.
    if (user.role === 'admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  }

  // role-only check passed (or no constraints at all → allow by default,
  // matches React convention that an empty gate is open).
  return true;
}

export default RoleGate;
